// Import necessary React hooks and Material-UI components
import React, { useState } from "react";
import {
  AppBar,
  Box,
  Container,
  IconButton,
  Link,
  Toolbar,
  Grid,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Button,
  MenuItem,
  Menu,
} from "@mui/material";

// Import configuration for website images
import { websiteImages } from "../../../config/config";

// Import Material-UI icons for navigation and user actions
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SearchIcon from "@mui/icons-material/Search";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import LoginIcon from "@mui/icons-material/Login";

// Import custom components for authentication and user management
import AuthPopup from "../../../components/AuthPopup";
import { useDispatch, useSelector } from "react-redux";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { Logout } from "@mui/icons-material";
import LogoutPopup from "../../../components/ClientLogoutPopup";
import ChangePasswordDialog from "../../../components/ChangePasswordDialog";
import { useNavigate } from "react-router-dom";

// Define the Headers component
const Headers = () => {
  // State for controlling mobile drawer visibility
  const [mobileOpen, setMobileOpen] = useState(false);
  // State for controlling login popup visibility
  const [loginOpen, setLoginOpen] = useState(false);
  // State for controlling user menu dropdown
  const [anchorEl, setAnchorEl] = useState(null);
  const isMenuOpen = Boolean(anchorEl);
  // Get user data from Redux store
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  // State for controlling logout popup visibility
  const [logoutpopupOpen, setLogoutpopupOpen] = useState(false);
  // State for controlling change password dialog visibility
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  // Hook for programmatic navigation
  const navigate = useNavigate();

  // Handle click on user menu
  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // Handle closing of user menu
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Toggle mobile drawer
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Define mobile drawer content
  const drawer = (
    <Box
      onClick={handleDrawerToggle}
      sx={{ textAlign: "center", backgroundColor: "#000", height: "100%" }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: { xs: 1.5, sm: 2 },
        }}
      >
        <Box
          component="img"
          src={websiteImages.websiteLogo}
          alt="RECOMP+"
          sx={{
            height: { xs: "25px", sm: "30px" },
            width: { xs: "110px", sm: "130px" },
          }}
        />
        <IconButton color="inherit" sx={{ color: "white" }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <List>
        {[
          "12–Week Programs",
          "The Bikini Method",
          "Premium Coaching",
          "Education",
          "Contact",
        ].map((item, i) => (
          <ListItem button key={i}>
            <ListItemText>
              <Link
                href="#"
                variant="body2"
                sx={{
                  color: "white",
                  textDecoration: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  fontSize: { xs: "16px", sm: "18px" },
                  py: 1,
                }}
              >
                {item}
                {["Premium Coaching", "Contact"].includes(item) ? null : (
                  <ArrowForwardIcon
                    fontSize="small"
                    sx={{ color: "#00ffff" }}
                  />
                )}
              </Link>
            </ListItemText>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  // Get subscription and chat status from Redux store
  const purchasedSubscription = useSelector(
    (state) => state.auth.purchasedSubscription
  );
  const chatEnabled = useSelector((state) => state.auth.chatEnabled);
  const isBlueprintSubscription = useSelector((state) => state.auth.is_blueprint_subscription);
  const subscriptionHasDocument = useSelector((state) => state.auth.subscription_has_document);

  console.log("isBlueprintSubscription", isBlueprintSubscription);  
  // Handle opening of subscription details or PDF
  const handleOpenPdf = () => {
    if (purchasedSubscription) {
      navigate("/subscription-details");
    }
  };

  // Render the header component
  return (
    <>
      <div>
        <Box
          className="user-container"
          sx={{
            backgroundImage: `url(${websiteImages.headerBg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
            alignItems: "center",
            px: { xs: 1, sm: 2, md: 3 },
            pb: { xs: 4, sm: 6, md: 8, lg: 10 },
            minHeight: { xs: "70vh", sm: "80vh", md: "100vh" },
          }}
        >
          <AppBar
            position="absolute"
            sx={{
              backgroundColor: "transparent",
              boxShadow: "none",
              top: { xs: "10px", sm: "20px", md: "34px" },
              left: "0px",
            }}
            className="container"
          >
            <Toolbar
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: { xs: 1, sm: 2, md: 3 },
                minHeight: { xs: "56px", sm: "64px" },
              }}
            >
              {/* Logo Section */}
              <Link href="/">
                <Box
                  component="img"
                  src={websiteImages.websiteLogo}
                  alt="RECOMP+"
                  sx={{
                    height: { xs: "24px", sm: "28px", md: "30px" },
                    width: { xs: "104px", sm: "120px", md: "130px" },
                  }}
                />
              </Link>

              {/* Desktop Navigation Menu */}
              <Box
                sx={{ display: { xs: "none", md: "flex" } }}
                className="nav-menu"
              >
                {[
                  "12–Week Programs",
                  "The Bikini Method",
                  "Premium Coaching",
                  "Education",
                  "Contact",
                ].map((item, i) => (
                  <Link
                    key={i}
                    href="#"
                    variant="body2"
                    sx={{
                      color: "white",
                      textDecoration: "none",
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      ml: i === 0 ? 0 : { md: 1.5, lg: 2 },
                      fontSize: { md: "0.875rem", lg: "1rem" },
                    }}
                  >
                    {item}
                    {["Premium Coaching", "Contact"].includes(item) ? null : (
                      <ArrowForwardIcon
                        fontSize="small"
                        sx={{ color: "#00ffff" }}
                      />
                    )}
                  </Link>
                ))}
              </Box>

              {/* Icons and Mobile Menu Section */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {/* Conditional Rendering for Authenticated/Un-authenticated Users */}
                {user ? (
                  <>
                    {/* Desktop Welcome Button */}
                    <Button
                      onClick={handleMenuClick}
                      sx={{
                        display: { xs: "none", md: "flex" },
                        color: "white",
                        border: "1px solid #00ffff",
                        textTransform: "none",
                        ml: 2,
                        fontWeight: 500,
                        fontSize: { md: "0.8rem", lg: "0.9rem" },
                        px: { md: 1.5, lg: 2 },
                        py: { md: 0.5, lg: 0.75 },
                        "&:hover": {
                          backgroundColor: "#00ffff",
                          color: "#000",
                        },
                      }}
                    >
                      Welcome, {user.name}
                    </Button>

                    {/* Mobile User Icon */}
                    <IconButton
                      onClick={handleMenuClick}
                      color="inherit"
                      sx={{
                        display: { xs: "flex", md: "none" },
                        ml: 1,
                        color: "white",
                        padding: { xs: "6px", sm: "8px" },
                      }}
                    >
                      <AccountCircleIcon fontSize="small" />
                    </IconButton>

                    {/* User Dropdown Menu */}
                    <Menu
                      anchorEl={anchorEl}
                      open={isMenuOpen}
                      onClose={handleMenuClose}
                      disableScrollLock
                      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                      transformOrigin={{ vertical: "top", horizontal: "left" }}
                      PaperProps={{
                        sx: {
                          backgroundColor: "#fff",
                          color: "#000",
                          borderRadius: 1,
                          margin: "14px 0 0 0",
                          padding: "0px",
                          minWidth: { xs: "120px", sm: "140px" },
                        },
                      }}
                    >
                      <MenuItem
                        onClick={() => {
                          handleMenuClose();
                          navigate("/profile");
                        }}
                        sx={{
                          borderBottom: "1px dotted #2b2a2aff",
                          fontSize: { xs: "0.875rem", sm: "1rem" },
                          py: { xs: 0.75, sm: 1 },
                          "&:hover": {
                            backgroundColor: "#00ffff",
                            color: "#000",
                          },
                        }}
                      >
                        Profile
                      </MenuItem>
                      <MenuItem
                        sx={{
                          borderBottom: "1px dotted #2b2a2aff",
                          fontSize: { xs: "0.875rem", sm: "1rem" },
                          py: { xs: 0.75, sm: 1 },
                          "&:hover": {
                            backgroundColor: "#00ffff",
                            color: "#000",
                          },
                        }}
                        onClick={() => {
                          handleMenuClose();
                          setChangePasswordOpen(true);
                        }}
                      >
                        Change Password
                      </MenuItem>
                      <MenuItem
                        sx={{
                          fontSize: { xs: "0.875rem", sm: "1rem" },
                          py: { xs: 0.75, sm: 1 },
                          "&:hover": {
                            backgroundColor: "#00ffff",
                            color: "#000",
                          },
                        }}
                        onClick={() => {
                          handleMenuClose();
                          setLogoutpopupOpen(true);
                        }}
                      >
                        Logout
                      </MenuItem>
                    </Menu>
                  </>
                ) : (
                  <>
                    {/* Desktop Login Button */}
                    <Button
                      onClick={() => setLoginOpen(true)}
                      startIcon={<LoginIcon fontSize="small" />}
                      sx={{
                        display: { xs: "none", md: "flex" },
                        color: "white",
                        border: "1px solid #00ffff",
                        textTransform: "none",
                        ml: 2,
                        fontWeight: 500,
                        fontSize: { md: "0.8rem", lg: "0.9rem" },
                        px: { md: 1.5, lg: 2 },
                        py: { md: 0.5, lg: 0.75 },
                        "&:hover": {
                          backgroundColor: "#00ffff",
                          color: "#000",
                        },
                      }}
                    >
                      Login / Sign Up
                    </Button>
                    {/* Mobile Login Icon */}
                    <IconButton
                      onClick={() => setLoginOpen(true)}
                      color="inherit"
                      sx={{
                        display: { xs: "flex", md: "none" },
                        ml: 1,
                        padding: { xs: "6px", sm: "8px" },
                      }}
                    >
                      <LoginIcon fontSize="small" />
                    </IconButton>
                  </>
                )}

                {/* Mobile Menu Toggle Icon */}
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{
                    display: { md: "none" },
                    padding: { xs: "6px", sm: "8px" },
                  }}
                >
                  <MenuIcon fontSize="small" />
                </IconButton>
              </Box>
            </Toolbar>
          </AppBar>

          {/* Mobile Drawer Component */}
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: "block", md: "none" },
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                width: { xs: 250, sm: 280 },
                backgroundColor: "#000",
              },
            }}
          >
            {drawer}
          </Drawer>

          {/* Authentication Popups */}
          <AuthPopup open={loginOpen} onClose={() => setLoginOpen(false)} />
          <LogoutPopup
            open={logoutpopupOpen}
            onClose={() => setLogoutpopupOpen(false)}
          />
          <ChangePasswordDialog
            open={changePasswordOpen}
            onClose={() => setChangePasswordOpen(false)}
          />

          {/* Hero Section */}
          <div className="container">
            <Grid
              container
              spacing={{ xs: 2, sm: 3, md: 4 }}
              sx={{ justifyContent: "space-around" }}
            >
              <Grid item size={{ xs: 12, md: 6 }}>
                <Typography
                  variant="h2"
                  sx={{
                    color: "white",
                    fontWeight: 700,
                    fontSize: {
                      xs: "1rem",
                      sm: "1.5rem",
                      md: "2.5rem",
                      lg: "3.5rem",
                      xl: "4.5rem",
                    },
                    maxWidth: "600px",
                    lineHeight: { xs: 1.2, sm: 1.15, md: 1.1 },
                    textAlign: "start",
                    mt: { xs: 0.5, sm: 1 },
                  }}
                >
                  16 Years of Body Recomposition Coaching — Now Online
                </Typography>
              </Grid>

              <Grid
                item
                size={{ xs: 12, md: 6 }}
                sx={{
                  display: "flex",
                  justifyContent: { xs: "flex-start", md: "flex-end" },
                  alignItems: "end",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "end",
                    gap: { xs: 1, sm: 1.5, md: 2 },
                    mt: { xs: 2, sm: 4, md: 8, lg: 12, xl: 16 },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: {
                        xs: "50px",
                        sm: "70px",
                        md: "90px",
                        lg: "100px",
                      },
                      fontWeight: 300,
                      color: "#00ffff",
                      lineHeight: 1,
                    }}
                  >
                    //
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "#fff",
                      fontWeight: "500",
                      fontSize: {
                        xs: "11px",
                        sm: "13px",
                        md: "14px",
                        lg: "16px",
                      },
                      maxWidth: { xs: 320, sm: 320, md: 320 },
                      marginBottom: { xs: "8px", sm: "12px", md: "18px" },
                      lineHeight: { xs: 1.3, sm: 1.35, md: 1.4 },
                      textAlign: "start",
                    }}
                  >
                    Get leaner, stronger, and healthier with a proven system
                    that adapts to your life — not the other way around.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </div>

          {/* Call-to-Action Buttons and Tagline */}
          <div className="container">
            <Box
              sx={{
                mt: { xs: 2, sm: 3, md: 4, lg: 6 },
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 1.5, sm: 2 },
                alignItems: { xs: "flex-start", sm: "center" },
                justifyContent: { xs: "start", sm: "start", md: "center" },
              }}
            >
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#00ffff",
                  color: "#000",
                  fontWeight: 600,
                  textTransform: "none",
                  borderRadius: "25px",
                  px: { xs: 2.5, sm: 3, md: 4 },
                  py: { xs: 0.75, sm: 1, md: 1.5 },
                  fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" },
                  minWidth: { xs: "140px", sm: "auto" },
                  "&:hover": {
                    backgroundColor: "#00ffff",
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 12px rgba(0, 198, 194, 0.3)",
                  },
                }}
                onClick={() => {
                  const section = document.getElementById(
                    "subscription-section"
                  );
                  if (section) {
                    section.scrollIntoView({ behavior: "smooth" });
                  }
                }}
              >
                Start Recomp Now
              </Button>
              {purchasedSubscription && subscriptionHasDocument && (
                <Button
                  variant="outlined"
                  sx={{
                    borderRadius: "25px",
                    borderColor: "#00ffff",
                    color: "#fff",
                    fontWeight: 600,
                    textTransform: "none",
                    px: { xs: 2.5, sm: 3, md: 4 },
                    py: { xs: 0.75, sm: 1, md: 1.5 },
                    fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" },
                    minWidth: { xs: "140px", sm: "auto" },
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundColor: "#00ffff",
                      transform: "translateY(-2px)",
                      boxShadow: "0 4px 12px rgba(0, 198, 194, 0.3)",
                      color: "#000",
                    },
                  }}
                  onClick={handleOpenPdf}
                >
                  Get the Blueprint
                </Button>
              )}
            </Box>

            <Typography
              variant="body2"
              sx={{
                color: "#fff",
                fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.9rem" },
                fontWeight: 400,
                mt: { xs: 1.5, sm: 2 },
                textAlign: { xs: "start", sm: "center" },
                display: "flex",
                justifyContent: { xs: "start", sm: "start", md: "center" },
                maxWidth: { xs: "100%", sm: "80%", md: "100%" },
              }}
            >
              Trusted by athletes, parents, and professionals since 2009.
            </Typography>
          </div>
        </Box>
      </div>
    </>
  );
};

// Export the Headers component
export default Headers;
